import { useEffect, useMemo } from 'react';

import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  useColorScheme,
} from '@mui/material';
import scssVariables from '@styles/variables.module.scss';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';

import styles from './SidebarContent.module.scss';
import { useMenuItemActive } from '../../hooks/useMenuItemActive';
import { MenuList } from '../MenuList';
import { MenuListItem } from '../MenuListItem';
import { ProfileMenuItem } from '../ProfileMenuItem';
import { SidebarSkeletonLoader } from '../SidebarSkeletonLoader';
import { useGetUserMe } from '@/api/REST/userMe';
import { LogoFull } from '@/components/global/Logo';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import {
  logoutMenuItem,
  homeMenuPathRoute,
  menuItems,
  pageSettingsMenuItem,
  settingsMenuItem,
  accountSettingsMenuItem,
} from '@/constants/menuItems';
import {
  useHasProjectPortfolio,
  useHasEmployeePortfolio,
  useHasDashboardsReporting,
} from '@/hooks/featureFlags';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePageSettingsMenu } from '@/legacy/apis/menusAPI/pageSettingsAPI';
import { Typography } from '@/legacy/components/mui-components';
import { useGetPageSetting } from '@/legacy/stores/PageStore';
import { isDev } from '@/legacy/utils/env';
import { useSidebarStore } from '@/stores/sidebar';

export const SidebarContent = () => {
  const { mode, setMode } = useColorScheme();

  // Feature flag hooks
  const hasProjectPortfolio = useHasProjectPortfolio();
  const hasEmployeePortfolio = useHasEmployeePortfolio();
  const hasDashboardsReporting = useHasDashboardsReporting();

  // Feature flag map for generic filtering
  // Only includes flags that are used for menu item filtering
  const featureFlagMap = {
    [FEATURE_FLAGS.PROJECT_PORTFOLIO]: hasProjectPortfolio ?? false,
    [FEATURE_FLAGS.EMPLOYEE_PORTFOLIO]: hasEmployeePortfolio ?? false,
    [FEATURE_FLAGS.DASHBOARDS_REPORTING]: hasDashboardsReporting ?? false,
  } as const;

  const {
    setCurrentExpandedParentMenuItem,
    setCurrentHighlightedParentMenuItem,
    resetExpandedAndHighlightedMenuItems,
  } = useSidebarStore();

  const { pathname } = useLocation();
  const { error: pageSettingError, isPageSettingMenuEmpty } =
    usePageSettingsMenu();
  const pageSettingsUrl = useGetPageSetting();
  const { t } = useTranslation('navigation');

  const { data: userMeData, isLoading, isError } = useGetUserMe();

  const userPagePermissions = useMemo(() => {
    return (
      userMeData?.permittedPages?.map(
        (permission) => permission.pageIdentifier,
      ) ?? []
    );
  }, [userMeData?.permittedPages]);

  const { filteredMenuItems, expandableMenuItems } = useMemo(() => {
    if (isLoading || isError || !userPagePermissions.length) {
      return { filteredMenuItems: [], expandableMenuItems: [] };
    }

    const filtered = menuItems
      .map((item) => {
        if (!item.items) {
          return item;
        } else {
          // If it has child items, check if user has permission for any child to include the parent
          const filteredChildList = item.items.filter((child) => {
            // Filter by backend permission
            const hasPermission =
              child.requiredPagePermission === null
                ? true // No permission required for child, include it
                : userPagePermissions.includes(child.requiredPagePermission);

            if (!hasPermission) {
              return false;
            }

            // Filter by feature flags (generic check)
            if (child.requiredFeatureFlag) {
              const featureFlagEnabled =
                featureFlagMap[child.requiredFeatureFlag] ?? false;
              if (!featureFlagEnabled) {
                return false;
              }
            }

            if (child.hiddenOnFeatureFlag) {
              const featureFlagEnabled =
                featureFlagMap[child.hiddenOnFeatureFlag] ?? false;
              if (featureFlagEnabled) {
                return false;
              }
            }

            return true;
          });

          return {
            ...item,
            items: filteredChildList, // Update the item with filtered children
          };
        }
      })
      .filter((item) => {
        if (!item) {
          return false; // Exclude null items (those that were filtered out)
        }

        if (item.items && item.items.length === 0 && !item.path) {
          return false; // If it has no children after filtering and itself is not visitable, exclude it
        }

        // Check backend permission
        const hasPermission = item.requiredPagePermission
          ? userPagePermissions.includes(item.requiredPagePermission)
          : true;

        if (!hasPermission) {
          return false;
        }

        // Check feature flag (for top-level menu items)
        if (item.requiredFeatureFlag) {
          const featureFlagEnabled =
            featureFlagMap[item.requiredFeatureFlag] ?? false;
          if (!featureFlagEnabled) {
            return false;
          }
        }

        if (item.hiddenOnFeatureFlag) {
          const featureFlagEnabled =
            featureFlagMap[item.hiddenOnFeatureFlag] ?? false;
          if (featureFlagEnabled) {
            return false;
          }
        }

        if (!item.path && item.items) {
          // If it has no path itself, but has children its only used as a group item and should be included, unless previous checks already excluded it
          return true;
        }

        return true;
      });

    const expandable = filtered.filter(
      (item) => item.items && item.items.length > 0,
    );

    return { filteredMenuItems: filtered, expandableMenuItems: expandable };
  }, [
    isLoading,
    isError,
    userPagePermissions,
    hasProjectPortfolio,
    hasEmployeePortfolio,
    hasDashboardsReporting,
  ]);

  const hasAccessToSystemAdministration = useMemo(() => {
    return userPagePermissions.some((permission) =>
      permission.startsWith('SystemAdministration'),
    );
  }, [userPagePermissions]);

  const isMobile = useIsMobile();

  const { isMenuItemActive, isExpandableItemActiveFallback } =
    useMenuItemActive();

  // set the initially expanded menu group based on the current route
  useEffect(() => {
    const initiallyExpandedMenuListItem = expandableMenuItems.find((item) => {
      const activeChild = item.items
        ? item.items.some((subItem) => isMenuItemActive(subItem))
        : false;

      if (activeChild) {
        return true;
      }

      return isExpandableItemActiveFallback(item);
    });

    if (initiallyExpandedMenuListItem) {
      setCurrentExpandedParentMenuItem(initiallyExpandedMenuListItem.id);
      setCurrentHighlightedParentMenuItem(initiallyExpandedMenuListItem.id);
    } else {
      resetExpandedAndHighlightedMenuItems();
    }
  }, [pathname, expandableMenuItems]);

  // Early return after all hooks have been called
  if (!mode) {
    return null;
  }

  return (
    <Stack
      className={cx(styles.SidebarContent, {
        [styles.SidebarContentMobile]: isMobile,
      })}
      component="nav"
      gap={0}
    >
      {isMobile && (
        <div className={cx(styles.SidebarLogoWrapper)}>
          <NavLink
            to={homeMenuPathRoute || '/'}
            data-automation-id="MainNavigationHomeLink"
          >
            <LogoFull />
          </NavLink>
        </div>
      )}

      <div className={styles.SidebarMainWrapper}>
        {isLoading || isError ? (
          <SidebarSkeletonLoader />
        ) : (
          <>
            <div className={styles.SidebarMenuOverflowContainer}>
              <Stack gap={2.2}>
                <MenuList menuItems={filteredMenuItems} />
                <Stack gap={1.4}>
                  <Typography
                    className={styles.MenuListTitle}
                    component="span"
                    variant="label"
                    color={scssVariables.ColorBlack60}
                  >
                    {t('SettingsHeader')}
                  </Typography>

                  {!pageSettingError &&
                  !isPageSettingMenuEmpty &&
                  pageSettingsUrl ? (
                    <MenuListItem
                      label={pageSettingsMenuItem.label}
                      id={pageSettingsMenuItem.id}
                      iconName={pageSettingsMenuItem.icon}
                      to={pageSettingsUrl}
                      isActive={
                        pathname.startsWith('/page-settings') ||
                        pathname.startsWith('/PersonalPageSettings')
                      }
                    />
                  ) : null}
                  {hasAccessToSystemAdministration ? (
                    <MenuListItem
                      label={settingsMenuItem.label}
                      id={settingsMenuItem.id}
                      iconName={settingsMenuItem.icon}
                      to={settingsMenuItem.path}
                      isActive={
                        (pathname.startsWith('/Settings') &&
                          !pathname.startsWith('/Settings/UserSettings')) ||
                        pathname.startsWith('/system-administration/') ||
                        pathname === '/Employee/EmployeeType'
                      }
                    />
                  ) : null}
                  {userMeData?.customerPortalUrl ? (
                    <MenuListItem
                      label={accountSettingsMenuItem.label}
                      id={accountSettingsMenuItem.id}
                      iconName={accountSettingsMenuItem.icon}
                      to={userMeData?.customerPortalUrl}
                      target={'_blank'}
                    />
                  ) : null}
                </Stack>
              </Stack>
            </div>
            <div className={styles.SidebarMenuBottom}>
              <Stack gap={0.6}>
                <ProfileMenuItem />
                <MenuListItem
                  to={logoutMenuItem.path}
                  label={logoutMenuItem.label}
                  id={logoutMenuItem.id}
                  iconName={logoutMenuItem.icon}
                  isActive={false}
                  isExpandable={false}
                />
              </Stack>
              {isDev && (
                <RadioGroup
                  aria-labelledby="demo-theme-toggle"
                  name="theme-toggle"
                  row
                  value={mode}
                  onChange={(event) =>
                    setMode(event.target.value as 'system' | 'light' | 'dark')
                  }
                >
                  <FormControlLabel
                    value="system"
                    control={<Radio />}
                    label="🏠"
                  />
                  <FormControlLabel
                    value="light"
                    control={<Radio />}
                    label="☀️"
                  />
                  <FormControlLabel
                    value="dark"
                    control={<Radio />}
                    label="🌜"
                  />
                </RadioGroup>
              )}
            </div>
          </>
        )}
      </div>
    </Stack>
  );
};
